<template>
    <div class="tlc-page">
        <!-- 1. BỘ LỌC THÔNG MINH (Top Card) -->
        <div class="tlc-filter-card">
            <!-- Header của Bộ lọc -->
            <div class="tlc-filter-header">
                <div class="tlc-filter-icon">
                    <i class="el-icon-trophy"></i>
                </div>
                <div class="tlc-filter-titles">
                    <h3 class="tlc-filter-title">Bộ lọc thông minh</h3>
                    <p class="tlc-filter-subtitle">Kết quả tự cập nhật ngay khi bạn đổi điều kiện</p>
                </div>
            </div>

            <!-- Các bộ lọc: Loại task, Trạng thái, Teamlead (nếu có quyền) -->
            <div class="tlc-filter-controls">
                <!-- Bộ lọc Teamlead (dành cho HR hoặc view_all) -->
                <div v-if="showUserFilter" class="tlc-filter-group">
                    <label class="tlc-filter-label">Teamlead</label>
                    <el-select
                        v-model="filterUserId"
                        size="small"
                        filterable
                        clearable
                        placeholder="Tất cả Teamlead"
                        class="tlc-select"
                        @change="onUserFilterChange"
                    >
                        <el-option
                            v-for="tl in tlcTeamLeads"
                            :key="tl.id"
                            :label="tl.name || `#${tl.id}`"
                            :value="tl.id"
                        >
                            <span>{{ tl.name }}</span>
                            <span v-if="tl.code || tl.team_name" class="tlc-opt-sub">
                                {{ tl.code ? `(${tl.code})` : '' }} {{ tl.team_name || '' }}
                            </span>
                        </el-option>
                    </el-select>
                </div>
            </div>

            <!-- Cute Mascot (Chú cáo đáng yêu ở góc trên bên phải) -->
            <div class="tlc-mascot" title="Edutalk Calendar Mascot">
                <svg width="84" height="74" viewBox="0 0 120 105" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <!-- Tai trái -->
                    <path d="M22 62L8 18C8 18 36 24 45 42" fill="#E86C2B" />
                    <path d="M22 55L14 26C14 26 32 30 38 42" fill="#FFE2D1" />
                    <!-- Tai phải -->
                    <path d="M98 62L112 18C112 18 84 24 75 42" fill="#E86C2B" />
                    <path d="M98 55L106 26C106 26 88 30 82 42" fill="#FFE2D1" />
                    <!-- Đầu cáo -->
                    <ellipse cx="60" cy="58" rx="46" ry="38" fill="#F47D31" />
                    <!-- Má & cằm lông trắng -->
                    <path d="M24 64C20 74 32 94 60 94C88 94 100 74 96 64C86 68 76 60 60 74C44 60 34 68 24 64Z" fill="#FFFFFF" />
                    <!-- Mắt to đáng yêu -->
                    <!-- Mắt trái -->
                    <ellipse cx="44" cy="54" rx="7" ry="9" fill="#2C1B18" />
                    <circle cx="42" cy="51" r="2.8" fill="#FFFFFF" />
                    <circle cx="46" cy="56" r="1.2" fill="#FFFFFF" />
                    <!-- Mắt phải -->
                    <ellipse cx="76" cy="54" rx="7" ry="9" fill="#2C1B18" />
                    <circle cx="74" cy="51" r="2.8" fill="#FFFFFF" />
                    <circle cx="78" cy="56" r="1.2" fill="#FFFFFF" />
                    <!-- Mũi nhỏ -->
                    <polygon points="60,65 56,62 64,62" fill="#2C1B18" />
                    <!-- Miệng cười -->
                    <path d="M56 67C58 69 60 69 60 67C60 69 62 69 64 67" stroke="#2C1B18" stroke-width="1.5" stroke-linecap="round" />
                    <!-- Má hồng -->
                    <ellipse cx="32" cy="62" rx="4.5" ry="2.5" fill="#FFA5A5" opacity="0.8" />
                    <ellipse cx="88" cy="62" rx="4.5" ry="2.5" fill="#FFA5A5" opacity="0.8" />
                    <!-- Hai bàn chân bám mép thẻ -->
                    <ellipse cx="34" cy="98" rx="10" ry="6" fill="#F47D31" stroke="#FFFFFF" stroke-width="2" />
                    <ellipse cx="86" cy="98" rx="10" ry="6" fill="#F47D31" stroke="#FFFFFF" stroke-width="2" />
                </svg>
            </div>
        </div>

        <!-- 2. NỘI DUNG CHÍNH (2 Cột: Lịch Tháng bên trái & Nhiệm vụ trong ngày bên phải) -->
        <div class="tlc-main-layout" v-loading="tlcLoading">
            <!-- CỘT TRÁI: LỊCH THÁNG (Month Calendar) -->
            <div class="tlc-cal-card">
                <!-- Thanh điều hướng tháng -->
                <div class="tlc-cal-header">
                    <div class="tlc-cal-nav">
                        <button type="button" class="tlc-arrow-btn" title="Tháng trước" @click="shiftMonth(-1)">
                            <i class="el-icon-arrow-left"></i>
                        </button>
                        <h2 class="tlc-cal-title">
                            Tháng {{ currentMonthNum }} năm {{ currentYearNum }}
                        </h2>
                        <button type="button" class="tlc-arrow-btn" title="Tháng sau" @click="shiftMonth(1)">
                            <i class="el-icon-arrow-right"></i>
                        </button>
                    </div>

                    <div class="tlc-cal-tools">
                        <el-date-picker
                            v-model="currentMonth"
                            type="month"
                            format="MM/yyyy"
                            value-format="yyyy-MM"
                            size="small"
                            :clearable="false"
                            class="tlc-month-picker"
                            @change="onMonthChange"
                        />
                        <el-button size="small" plain class="tlc-today-btn" @click="goToToday">
                            <i class="el-icon-date"></i> Hôm nay
                        </el-button>
                    </div>
                </div>

                <!-- Lưới 7 ngày trong tuần -->
                <div class="tlc-grid-wrapper">
                    <!-- Tiêu đề các thứ: T2, T3, T4, T5, T6, T7, CN -->
                    <div class="tlc-dow-row">
                        <div v-for="dow in ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN']" :key="dow" class="tlc-dow-cell">
                            {{ dow }}
                        </div>
                    </div>

                    <!-- Các ô ngày trong tháng (5-6 hàng x 7 ngày) -->
                    <div class="tlc-days-grid">
                        <div
                            v-for="day in monthDays"
                            :key="day.date"
                            class="tlc-day-cell"
                            :class="{
                                'is-other-month': !day.isCurrentMonth,
                                'is-selected': day.date === selectedDate,
                                'is-today': day.isToday,
                                'is-weekend': day.isWeekend,
                            }"
                            @click="selectDay(day.date)"
                            @dblclick="openCreateActivity(day.date)"
                        >
                            <div class="tlc-day-cell__head">
                                <span class="tlc-day-num">{{ day.dayNum }}</span>
                                <span v-if="day.isToday" class="tlc-today-badge">Hôm nay</span>
                            </div>

                            <!-- Hiển thị các chấm / task trong ngày -->
                            <div class="tlc-day-cell__body">
                                <template v-if="dayTasksMap[day.date] && dayTasksMap[day.date].length">
                                    <div class="tlc-cell-chips">
                                        <div
                                            v-for="task in dayTasksMap[day.date].slice(0, 2)"
                                            :key="task.id || task._key"
                                            class="tlc-mini-chip"
                                            :style="getTaskChipStyle(task)"
                                            :title="formatTaskFullTooltip(task)"
                                            @click.stop="onEventClick(task)"
                                        >
                                            <span class="tlc-mini-chip__dot" :style="{ backgroundColor: getTaskColor(task).dot }"></span>
                                            <span v-if="formatTaskTime(task)" class="tlc-mini-chip__time" :style="{ color: getTaskColor(task).time }">
                                                {{ formatTaskTime(task) }}
                                            </span>
                                            <span class="tlc-mini-chip__text" :style="{ color: getTaskColor(task).text }">{{ task.task || task.title }}</span>
                                        </div>
                                        <div
                                            v-if="dayTasksMap[day.date].length > 2"
                                            class="tlc-more-chip"
                                            :title="`Còn ${dayTasksMap[day.date].length - 2} task khác`"
                                        >
                                            +{{ dayTasksMap[day.date].length - 2 }} task
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- CỘT PHẢI: NHIỆM VỤ TRONG NGÀY (Day Tasks Panel) -->
            <div class="tlc-day-card">
                <!-- Header của Nhiệm vụ trong ngày -->
                <div class="tlc-day-card__header">
                    <div class="tlc-day-card__titles">
                        <span class="tlc-day-card__subtitle">NHIỆM VỤ TRONG NGÀY</span>
                        <h3 class="tlc-day-card__title">{{ selectedDateFormatted }}</h3>
                    </div>

                    <!-- Nút tròn tạo task (+) màu xanh -->
                    <button
                        type="button"
                        class="tlc-add-circle-btn"
                        title="Tạo task mới cho ngày này"
                        @click="openCreateActivity(selectedDate)"
                    >
                        <i class="el-icon-plus"></i>
                    </button>
                </div>

                <!-- Body: Danh sách nhiệm vụ hoặc Empty State khi chưa có task -->
                <div class="tlc-day-card__body">
                    <!-- Trạng thái trống (Empty State) - Đúng chuẩn như ảnh mẫu -->
                    <div v-if="selectedDayTasks.length === 0" class="tlc-empty-state">
                        <div class="tlc-empty-icon">
                            <i class="el-icon-message"></i>
                        </div>
                        <h4 class="tlc-empty-title">Chưa có task nào trong ngày này</h4>
                        <p class="tlc-empty-desc">
                            Chọn ngày khác trên lịch hoặc tạo một task việc mới cho ngày này
                        </p>
                        <el-button
                            size="small"
                            class="tlc-create-task-btn"
                            @click="openCreateActivity(selectedDate)"
                        >
                            <i class="el-icon-plus"></i> Tạo task việc
                        </el-button>
                    </div>

                    <!-- Khi đã có task: hiển thị danh sách task -->
                    <div v-else class="tlc-task-list">
                        <div
                            v-for="task in selectedDayTasks"
                            :key="task.id || task._key"
                            class="tlc-task-card"
                            :style="{ borderLeftColor: getTaskColor(task).dot, borderLeftWidth: '4px' }"
                        >
                            <div class="tlc-task-card__head">
                                <span class="tlc-task-time" :style="{ color: getTaskColor(task).time }">
                                    <i class="el-icon-time"></i> {{ formatTaskTime(task) || (task.start_time ? task.start_time + (task.end_time ? ' - ' + task.end_time : '') : '08:00 - 09:00') }}
                                </span>
                                <span v-if="task.recruitment_interview_id" class="tlc-meta-item tlc-meta-item--interview">
                                    <i class="el-icon-video-camera"></i> Phỏng vấn
                                </span>
                            </div>

                            <div class="tlc-task-card__title" @click="onEventClick(task)">
                                <span class="tlc-task-card__dot" :style="{ backgroundColor: getTaskColor(task).dot }"></span>
                                <span class="tlc-task-card__name">{{ task.task || task.title || 'Công việc không tên' }}</span>
                            </div>

                            <div v-if="task.team_lead_name || task.recruitment_interview_id" class="tlc-task-card__meta">
                                <span v-if="task.team_lead_name" class="tlc-meta-item">
                                    <i class="el-icon-user"></i> {{ task.team_lead_name }}
                                </span>
                                <span v-if="task.recruitment_interview_id" class="tlc-meta-item tlc-meta-item--interview">
                                    <i class="el-icon-video-camera"></i> Phỏng vấn
                                </span>
                            </div>

                            <div class="tlc-task-card__footer">
                                <div class="tlc-task-btns" style="margin-left: auto;">
                                    <el-button type="text" size="mini" icon="el-icon-edit" @click="onEventClick(task)">
                                        Sửa
                                    </el-button>
                                    <el-button type="text" size="mini" icon="el-icon-delete" class="text-danger" @click="confirmDelete(task)">
                                        Xoá
                                    </el-button>
                                </div>
                            </div>
                        </div>

                        <el-button
                            size="small"
                            type="primary"
                            plain
                            class="w-100 mt-2"
                            icon="el-icon-plus"
                            @click="openCreateActivity(selectedDate)"
                        >
                            Thêm task việc mới
                        </el-button>
                    </div>
                </div>

                <!-- Footer trích dẫn truyền cảm hứng (như trong ảnh mẫu) -->
                <div class="tlc-day-card__footer">
                    <p class="tlc-quote">
                        Thành công được tạo nên từ những nỗ lực nhỏ kiên trì lặp lại mỗi ngày! 🍃
                    </p>
                </div>

                <!-- Zalo Floating Widget góc dưới phải (như trong ảnh mẫu) -->
                <div class="tlc-zalo-badge" title="Hỗ trợ Zalo">
                    <span class="tlc-zalo-text">Zalo</span>
                </div>
            </div>
        </div>

        <!-- Dialog Tạo / Sửa hoạt động / lịch -->
        <schedule-form-dialog
            :visible.sync="formDialogVisible"
            :show-team-lead="showUserFilter"
            :team-leads="tlcTeamLeads"
            :initial="formInitial"
            :editing="formEditing"
            :submitting="formSubmitting"
            :current-user-id="currentUserId"
            @submit="handleFormSubmit"
        />
    </div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
    TLC_GET_SCHEDULES,
    TLC_CREATE_SCHEDULE,
    TLC_UPDATE_SCHEDULE,
    TLC_DELETE_SCHEDULE,
} from "@/core/services/store/team-leads-calendar/teamLeadsCalendar.module";
import tlcRoleMixin from "./mixins/tlcRoleMixin";
import ScheduleFormDialog from "./components/ScheduleFormDialog.vue";
import { getTaskThemeColor } from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";

export default {
    name: "TeamLeadsCalendar",
    components: {
        ScheduleFormDialog,
    },
    mixins: [tlcRoleMixin],
    data() {
        return {
            // Tháng đang xem (định dạng YYYY-MM)
            currentMonth: moment().format("YYYY-MM"),
            // Ngày đang chọn (định dạng YYYY-MM-DD)
            selectedDate: moment().format("YYYY-MM-DD"),

            // Bộ lọc thông minh
            filterTaskType: "",
            filterUserId: null,

            // Dialog Tạo/Sửa
            formDialogVisible: false,
            formInitial: {},
            formEditing: false,
            formSubmitting: false,
        };
    },
    computed: {
        ...mapGetters([
            "tlcTeamLeads",
            "tlcEvents",
            "tlcLoading",
            "tlcViewAll",
        ]),
        showUserFilter() {
            return this.isHrDepartment || this.tlcViewAll === true;
        },
        currentMonthNum() {
            return moment(this.currentMonth, "YYYY-MM").format("M");
        },
        currentYearNum() {
            return moment(this.currentMonth, "YYYY-MM").format("YYYY");
        },
        /**
         * Tiêu đề ngày được format theo chuẩn Việt Nam:
         * VD: "Thứ Bảy, 01/11/2025"
         */
        selectedDateFormatted() {
            if (!this.selectedDate) return "";
            const m = moment(this.selectedDate, "YYYY-MM-DD");
            const dows = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
            const dowName = dows[m.day()];
            return `${dowName}, ${m.format("DD/MM/YYYY")}`;
        },
        /**
         * Danh sách các ngày trong lưới tháng (5 - 6 tuần x 7 ngày = 35 hoặc 42 ô)
         * Khởi đầu từ Thứ 2 (isoWeek) của tuần chứa ngày 1
         */
        monthDays() {
            const mStartMonth = moment(this.currentMonth, "YYYY-MM").startOf("month");
            const mEndMonth = moment(this.currentMonth, "YYYY-MM").endOf("month");
            const startGrid = mStartMonth.clone().startOf("isoWeek");
            const endGrid = mEndMonth.clone().endOf("isoWeek");

            const currentMonthStr = moment(this.currentMonth, "YYYY-MM").format("YYYY-MM");
            const todayStr = moment().format("YYYY-MM-DD");

            const days = [];
            let cur = startGrid.clone();
            while (cur.isBefore(endGrid) || cur.isSame(endGrid, "day")) {
                const dateStr = cur.format("YYYY-MM-DD");
                days.push({
                    date: dateStr,
                    dayNum: cur.date(),
                    isCurrentMonth: cur.format("YYYY-MM") === currentMonthStr,
                    isToday: dateStr === todayStr,
                    isWeekend: cur.isoWeekday() >= 6,
                });
                cur.add(1, "days");
            }
            return days;
        },
        /**
         * Danh sách events sau khi áp dụng bộ lọc Loại task và Trạng thái
         */
        filteredEvents() {
            let list = this.tlcEvents || [];

            // Lọc theo Loại task
            if (this.filterTaskType) {
                list = list.filter((e) => {
                    if (this.filterTaskType === "interview") {
                        return !!e.recruitment_interview_id || e.type === "interview" || (e.raw && e.raw.recruitment_interview_id);
                    }
                    if (this.filterTaskType === "task") {
                        return e.type === "task" || (!e.type && !e.recruitment_interview_id);
                    }
                    return e.type === this.filterTaskType;
                });
            }

            // Lọc theo User nếu có chọn trên dropdown
            if (this.showUserFilter && this.filterUserId) {
                list = list.filter((e) => {
                    return String(e.user_id) === String(this.filterUserId) || String(e.team_lead_id) === String(this.filterUserId);
                });
            }

            return list;
        },
        /**
         * Bản đồ gom nhóm task theo từng ngày: dayTasksMap[YYYY-MM-DD] = [task1, task2...]
         */
        dayTasksMap() {
            const map = {};
            (this.filteredEvents || []).forEach((ev) => {
                if (!ev || !ev.date) return;
                if (!map[ev.date]) {
                    map[ev.date] = [];
                }
                map[ev.date].push(ev);
            });
            // Sắp xếp các task trong từng ngày theo thứ tự giờ bắt đầu tăng dần
            Object.keys(map).forEach((d) => {
                map[d].sort((a, b) => {
                    const tA = (a && a.start_time) || "00:00";
                    const tB = (b && b.start_time) || "00:00";
                    return tA.localeCompare(tB);
                });
            });
            return map;
        },
        /**
         * Danh sách nhiệm vụ cho ngày đang được chọn (selectedDate)
         */
        selectedDayTasks() {
            return this.dayTasksMap[this.selectedDate] || [];
        },
    },
    mounted() {
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Lịch trình teamlead", icon: "far fa-calendar-alt" },
        ]);
        this.loadProfileDepartment();
        this.loadSchedules();
    },
    methods: {
        /**
         * Tải danh sách lịch trình từ API cho phạm vi toàn bộ các ngày hiển thị trên lưới
         */
        loadSchedules() {
            const mStart = moment(this.currentMonth, "YYYY-MM").startOf("month").startOf("isoWeek");
            const mEnd = moment(this.currentMonth, "YYYY-MM").endOf("month").endOf("isoWeek");

            const params = {
                date_range: [mStart.format("YYYY-MM-DD"), mEnd.format("YYYY-MM-DD")],
            };
            if (this.showUserFilter && this.filterUserId) {
                params.user_id = this.filterUserId;
            }

            this.$store
                .dispatch(TLC_GET_SCHEDULES, params)
                .catch(() => {});
        },
        onUserFilterChange() {
            this.loadSchedules();
        },
        shiftMonth(offset) {
            this.currentMonth = moment(this.currentMonth, "YYYY-MM")
                .add(offset, "months")
                .format("YYYY-MM");
            // Đặt ngày chọn sang ngày 1 của tháng mới
            this.selectedDate = `${this.currentMonth}-01`;
            this.loadSchedules();
        },
        onMonthChange(val) {
            if (val) {
                this.currentMonth = val;
                this.selectedDate = `${val}-01`;
                this.loadSchedules();
            }
        },
        goToToday() {
            this.currentMonth = moment().format("YYYY-MM");
            this.selectedDate = moment().format("YYYY-MM-DD");
            this.loadSchedules();
        },
        selectDay(date) {
            this.selectedDate = date;
            // Nếu ngày thuộc tháng khác thì tự động chuyển tháng hiển thị
            const monthOfDate = moment(date, "YYYY-MM-DD").format("YYYY-MM");
            if (monthOfDate !== this.currentMonth) {
                this.currentMonth = monthOfDate;
                this.loadSchedules();
            }
        },
        getTaskColor(task) {
            return getTaskThemeColor(task);
        },
        getTaskChipStyle(task) {
            const col = this.getTaskColor(task);
            return {
                backgroundColor: col.bg,
                borderColor: col.border,
            };
        },
        toHm(val) {
            if (!val) return "";
            const m = String(val).match(/^(\d{1,2}):(\d{2})/);
            return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
        },
        formatTaskTime(task) {
            if (!task) return "";
            const start = this.toHm(task.start_time || (task.raw && task.raw.start_time));
            const end = this.toHm(task.end_time || (task.raw && task.raw.end_time));
            if (start && end && start !== end) {
                return `${start} - ${end}`;
            }
            return start || end || "";
        },
        formatTaskFullTooltip(task) {
            if (!task) return "";
            const time = this.formatTaskTime(task);
            const title = task.task || task.title || "Công việc";
            const tl = task.team_lead_name ? ` (${task.team_lead_name})` : "";
            return time ? `${time}: ${title}${tl}` : `${title}${tl}`;
        },
        openCreateActivity(targetDate) {
            this.formEditing = false;
            this.formInitial = {
                date: targetDate || this.selectedDate,
                user_id: (this.showUserFilter && this.filterUserId) || this.currentUserId || undefined,
                start_time: "08:00",
                end_time: "09:00",
            };
            this.formDialogVisible = true;
        },
        onEventClick(ev) {
            this.formEditing = true;
            this.formInitial = {
                ...ev,
                user_id: ev.team_lead_id || ev.user_id,
                color: ev.color || (ev.raw && ev.raw.color) || "",
            };
            this.formDialogVisible = true;
        },
        confirmDelete(task) {
            const title = task.task || task.title || "nhiệm vụ này";
            this.$confirm(`Bạn có chắc chắn muốn xoá "${title}"?`, "Xác nhận xoá", {
                confirmButtonText: "Xoá",
                cancelButtonText: "Huỷ",
                type: "warning",
            })
                .then(async () => {
                    try {
                        await this.$store.dispatch(TLC_DELETE_SCHEDULE, task.id);
                        this.$message.success("Đã xoá nhiệm vụ thành công");
                        this.loadSchedules();
                    } catch (e) {
                        const msg = (e && e.data && e.data.message) || (e && e.message) || "Xoá thất bại";
                        this.$message.error(msg);
                    }
                })
                .catch(() => {});
        },
        async handleFormSubmit({ editing, id, payload }) {
            this.formSubmitting = true;
            try {
                if (editing && id) {
                    await this.$store.dispatch(TLC_UPDATE_SCHEDULE, { id, ...payload });
                } else {
                    await this.$store.dispatch(TLC_CREATE_SCHEDULE, payload);
                }
                this.$message.success(editing ? "Đã cập nhật lịch trình" : "Đã tạo hoạt động mới");
                this.formDialogVisible = false;
                this.loadSchedules();
            } catch (e) {
                const msg = (e && e.data && e.data.message) || (e && e.message) || "Lưu thất bại, vui lòng thử lại.";
                this.$message.error(msg);
            } finally {
                this.formSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
.tlc-page {
    padding: 16px 20px 40px;
    background: #f8fafc;
    min-height: calc(100vh - 70px);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: #1e293b;
    position: relative;
}

/* ============================================================
   1. BỘ LỌC THÔNG MINH (Top Card)
   ============================================================ */
.tlc-filter-card {
    background: #ffffff;
    border: 1px solid #eef2f6;
    border-radius: 14px;
    padding: 18px 24px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    position: relative;
    overflow: visible;
}

.tlc-filter-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
}

.tlc-filter-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #ede9fe;
    color: #7c3aed;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
}

.tlc-filter-titles {
    display: flex;
    flex-direction: column;
}

.tlc-filter-title {
    font-size: 14.5px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.3;
}

.tlc-filter-subtitle {
    font-size: 12px;
    color: #64748b;
    margin: 2px 0 0;
    line-height: 1.2;
}

.tlc-filter-controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
}

.tlc-filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.tlc-filter-label {
    font-size: 11.5px;
    font-weight: 600;
    color: #475569;
    margin: 0;
}

.tlc-select {
    width: 190px;
}

.tlc-opt-sub {
    float: right;
    color: #94a3b8;
    font-size: 12px;
}

/* Cute Mascot Cáo ở góc trên phải */
.tlc-mascot {
    position: absolute;
    right: 28px;
    top: -14px;
    width: 84px;
    height: 74px;
    pointer-events: none;
    z-index: 10;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.08));
    animation: mascotBob 4s ease-in-out infinite alternate;
}

@keyframes mascotBob {
    0% {
        transform: translateY(0);
    }
    100% {
        transform: translateY(-3px);
    }
}

/* ============================================================
   2. MAIN LAYOUT (2 CỘT)
   ============================================================ */
.tlc-main-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 20px;
    align-items: start;
    min-width: 0;
}

@media (max-width: 1180px) {
    .tlc-main-layout {
        grid-template-columns: minmax(0, 1fr);
    }
}

/* ============================================================
   CỘT TRÁI: LỊCH THÁNG (Month Calendar)
   ============================================================ */
.tlc-cal-card {
    background: #ffffff;
    border: 1px solid #eef2f6;
    border-radius: 14px;
    padding: 20px 22px 24px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);
    min-width: 0;
    overflow: hidden;
}

.tlc-cal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
}

.tlc-cal-nav {
    display: flex;
    align-items: center;
    gap: 12px;
}

.tlc-arrow-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.2s;
}

.tlc-arrow-btn:hover {
    border-color: #cbd5e1;
    color: #1e293b;
    background: #f8fafc;
}

.tlc-cal-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    letter-spacing: -0.01em;
}

.tlc-cal-tools {
    display: flex;
    align-items: center;
    gap: 10px;
}

.tlc-month-picker {
    width: 120px !important;
}

.tlc-today-btn {
    border-color: #e2e8f0;
    color: #475569;
}
.tlc-today-btn:hover {
    color: #4338ca;
    border-color: #c7d2fe;
}

/* Calendar Grid */
.tlc-grid-wrapper {
    display: flex;
    flex-direction: column;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    overflow: hidden;
    min-width: 0;
    width: 100%;
}

.tlc-dow-row {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    background: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
    min-width: 0;
    width: 100%;
}

.tlc-dow-cell {
    padding: 10px 4px;
    text-align: center;
    font-size: 12.5px;
    font-weight: 600;
    color: #64748b;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tlc-days-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    background: #f1f5f9;
    gap: 1px;
    min-width: 0;
    width: 100%;
}

.tlc-day-cell {
    background: #ffffff;
    min-height: 82px;
    padding: 7px 8px;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    position: relative;
    min-width: 0;
    overflow: hidden;
    box-sizing: border-box;
}

.tlc-day-cell:hover {
    background: #fcfdfe;
}

/* Khi ngày được chọn: viền bo tròn màu tím/indigo như ảnh mẫu */
.tlc-day-cell.is-selected {
    box-shadow: inset 0 0 0 2px #4f46e5;
    background: #ffffff;
    z-index: 2;
    border-radius: 8px;
}

/* Ngày thuộc tháng khác */
.tlc-day-cell.is-other-month {
    background: #fafbfc;
}
.tlc-day-cell.is-other-month .tlc-day-num {
    color: #94a3b8;
}

.tlc-day-cell__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
    min-width: 0;
}

.tlc-day-num {
    font-size: 12.5px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1;
}

.tlc-today-badge {
    font-size: 10px;
    background: #dbeafe;
    color: #1e40af;
    padding: 1px 4px;
    border-radius: 4px;
    font-weight: 600;
    flex-shrink: 0;
}

.tlc-day-cell__body {
    flex: 1;
    overflow: hidden;
    min-width: 0;
    width: 100%;
}

.tlc-cell-chips {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    width: 100%;
}

.tlc-mini-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    padding: 2.5px 6px;
    border-radius: 5px;
    border-width: 1px;
    border-style: solid;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
    min-width: 0;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
}
.tlc-mini-chip:hover {
    filter: brightness(0.96);
    transform: translateY(-0.5px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.tlc-mini-chip__dot {
    width: 6.5px;
    height: 6.5px;
    border-radius: 50%;
    flex-shrink: 0;
}

.tlc-mini-chip__time {
    font-size: 9.5px;
    font-weight: 700;
    flex-shrink: 0;
    line-height: 1;
    white-space: nowrap;
}

.tlc-mini-chip__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
    font-weight: 600;
}

.tlc-more-chip {
    font-size: 10px;
    color: #64748b;
    font-weight: 600;
    padding-left: 2px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ============================================================
   CỘT PHẢI: NHIỆM VỤ TRONG NGÀY (Day Tasks Panel)
   ============================================================ */
.tlc-day-card {
    background: #ffffff;
    border: 1px solid #eef2f6;
    border-radius: 14px;
    padding: 22px 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.02);
    min-height: 480px;
    display: flex;
    flex-direction: column;
    position: relative;
}

.tlc-day-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
}

.tlc-day-card__titles {
    display: flex;
    flex-direction: column;
}

.tlc-day-card__subtitle {
    font-size: 11px;
    font-weight: 700;
    color: #4338ca;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 4px;
}

.tlc-day-card__title {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

/* Nút tròn màu xanh có dấu cộng */
.tlc-add-circle-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #3b82f6;
    color: #ffffff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 15px;
    transition: all 0.2s;
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
}

.tlc-add-circle-btn:hover {
    background: #2563eb;
    transform: scale(1.05);
}

.tlc-day-card__body {
    flex: 1;
    display: flex;
    flex-direction: column;
}

/* Empty State - Hoàn toàn tương tự ảnh mẫu */
.tlc-empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 30px 16px;
}

.tlc-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #eff6ff;
    border: 1px solid #dbeafe;
    color: #3b82f6;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 16px;
}

.tlc-empty-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
}

.tlc-empty-desc {
    font-size: 12px;
    color: #64748b;
    margin: 0 0 18px;
    line-height: 1.5;
    max-width: 230px;
}

.tlc-create-task-btn {
    background: #ffffff;
    border: 1px solid #cbd5e1;
    color: #334155;
    font-weight: 500;
    border-radius: 6px;
    padding: 8px 16px;
}

.tlc-create-task-btn:hover {
    color: #3b82f6;
    border-color: #bfdbfe;
    background: #f8fafc;
}

/* Danh sách task */
.tlc-task-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.tlc-task-card {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
    background: #ffffff;
    transition: all 0.2s;
    min-width: 0;
    overflow: hidden;
}

.tlc-task-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.tlc-task-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    min-width: 0;
}

.tlc-task-time {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 500;
}

.tlc-task-card__title {
    font-size: 13.5px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
    cursor: pointer;
    line-height: 1.4;
    display: flex;
    align-items: flex-start;
    gap: 7px;
    min-width: 0;
}
.tlc-task-card__title:hover {
    color: #3b82f6;
}
.tlc-task-card__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 5px;
}
.tlc-task-card__name {
    flex: 1;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.tlc-task-card__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 11.5px;
    color: #64748b;
    margin-bottom: 8px;
}

.tlc-meta-item--interview {
    color: #4f46e5;
    font-weight: 600;
}

.tlc-task-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 8px;
    border-top: 1px dashed #f1f5f9;
}

.tlc-action-btn {
    font-size: 11.5px;
    color: #64748b;
    cursor: pointer;
}
.tlc-action-btn:hover {
    color: #3b82f6;
}

.tlc-dot-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 4px;
}
.tlc-dot-indicator--yellow {
    background: #eab308;
}
.tlc-dot-indicator--blue {
    background: #3b82f6;
}
.tlc-dot-indicator--green {
    background: #10b981;
}

.tlc-task-btns {
    display: flex;
    align-items: center;
    gap: 6px;
}

/* Footer Quote (Như trong ảnh mẫu) */
.tlc-day-card__footer {
    margin-top: auto;
    padding-top: 24px;
    text-align: center;
}

.tlc-quote {
    font-size: 11px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
    font-style: italic;
}

/* Zalo Floating Badge (Góc dưới phải) */
.tlc-zalo-badge {
    position: absolute;
    bottom: 14px;
    right: 14px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #0068ff;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 104, 255, 0.3);
}

.w-100 {
    width: 100%;
}
.mt-2 {
    margin-top: 8px;
}
.text-danger {
    color: #ef4444 !important;
}
</style>
